import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Col } from 'react-bootstrap';
import { DateTime } from 'luxon';

import { useAppSelector } from 'hooks';
import { CalendarIcon } from 'assets/icons';
import { formatRecurrence } from 'utils/Time';
import { DropdownGroup } from 'components/Forms';

import ActionsTask from '../../../ActionsTask';
import DeleteQuestionnaireModal from './DeleteQuestionnaireModal';

export const QuestionnaireContent = ({
  questionnaire
}: {
  questionnaire: ICarePlanObservationTask;
}) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);

  return (
    <div className="p-3 d-flex align-items-center">
      <div className="w-100">
        <div className="mb-2 text-sm font-weight-bolder">
          {intl.formatMessage({ id: questionnaire.title })}
        </div>
        <div className="text-sm mb-0 d-flex align-items-center">
          <div className="me-2">{intl.formatMessage({ id: 'lastAnsweredKey' })}:</div>
          {questionnaire.last_survey_session
            ? DateTime.fromMillis(Number(questionnaire.last_survey_session))
                .setLocale(locale)
                .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)
            : '-'}
        </div>
        <div className="text-sm font-weight-bolder mt-2">
          <CalendarIcon className="icon me-2" style={{ fontSize: '14px' }} />
          {!questionnaire.isScheduled
            ? intl.formatMessage({ id: 'unscheduledKey' })
            : questionnaire.recurrence_hr &&
              formatRecurrence(
                questionnaire.recurrence_hr,
                locale,
                intl.formatMessage({ id: 'atKey' })
              )}
        </div>
        {questionnaire.action.survey?.actions_on_task &&
          questionnaire.action.survey?.actions_on_task.length > 0 && (
            <ActionsTask tasks={questionnaire.action.survey?.actions_on_task} />
          )}
      </div>
    </div>
  );
};

interface IQuestionnaireContainerProps {
  questionnaire: ICarePlanQuestionnaire;
  onDeleteQuestionnaire: (id: string) => any;
  handleEdit: any;
  questionnairesData: ISurvey[];
  carePlanTemplate?: ICarePlanTemplate;
  dataTestId: string;
  onShowEditModal: () => void;
  disabled?: boolean;
}

const Questionnaire = ({
  questionnaire,
  onDeleteQuestionnaire,
  handleEdit,
  carePlanTemplate,
  dataTestId,
  onShowEditModal,
  disabled
}: IQuestionnaireContainerProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';

  const handleDeleteQuestionnaire = () => {
    setShowDeleteModal(false);
    onDeleteQuestionnaire(questionnaire.id);
  };

  return (
    <Col lg={6} xl={4} className="p-3" data-test={dataTestId}>
      <div className="bg-light">
        {!isTemplateGlobal && (
          <DropdownGroup
            dataTestId={dataTestId}
            onShowEditModal={onShowEditModal}
            onShowDeleteModal={() => setShowDeleteModal(true)}
            disabled={disabled}
          />
        )}
        <QuestionnaireContent questionnaire={questionnaire} />
      </div>

      <DeleteQuestionnaireModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        questionnaire={questionnaire}
        onDeleteQuestionnaire={handleDeleteQuestionnaire}
        carePlanTemplate={carePlanTemplate}
      />
    </Col>
  );
};

export default Questionnaire;
